<template>
  <div class="modal fade upload-plot-modal" tabindex="-1" role="dialog" aria-labelledby="uploadPlotModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-info text-white">
          <h5 class="modal-title text-uppercase text-white" id="uploadPlotModalLabel">
            <i class="mdi mdi-file-upload me-2"></i>Upload Plot Rent Data
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" :disabled="uploading"></button>
        </div>

        <div class="row" style="margin-left: 10px;margin-top: 10px">
          <a :href="templateUrl">Download Template</a>
        </div>

        <div class="modal-body">
          <!-- Upload Progress Section -->
          <div v-if="uploading" class="upload-progress-section mb-4">
            <div class="alert alert-info">
              <h6 class="mb-3">
                <i class="bx bx-upload me-2"></i>
                Upload in Progress... Please do not close this window
              </h6>

              <!-- Progress Bar -->
              <div class="progress mb-3" style="height: 30px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated"
                     role="progressbar"
                     :style="{width: uploadProgress.percentage + '%'}"
                     :class="uploadProgress.percentage === 100 ? 'bg-success' : 'bg-info'">
                  {{ uploadProgress.percentage }}%
                </div>
              </div>

              <!-- Upload Statistics -->
              <div class="row text-center mb-3">
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0 text-primary">{{ uploadProgress.currentBatch }}</h4>
                    <small class="text-muted">Current Batch</small>
                  </div>
                </div>
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0 text-success">{{ uploadProgress.totalUploaded }}</h4>
                    <small class="text-muted">Uploaded</small>
                  </div>
                </div>
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0 text-danger">{{ uploadProgress.totalFailed }}</h4>
                    <small class="text-muted">Failed</small>
                  </div>
                </div>
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0">{{ uploadProgress.totalRecords }}</h4>
                    <small class="text-muted">Total Records</small>
                  </div>
                </div>
              </div>

              <!-- Time Estimation -->
              <div class="row text-center">
                <div class="col-6">
                  <p class="mb-0">
                    <i class="bx bx-time me-1"></i>
                    <strong>Elapsed:</strong> {{ formatTime(uploadProgress.elapsedTime) }}
                  </p>
                </div>
                <div class="col-6">
                  <p class="mb-0">
                    <i class="bx bx-hourglass me-1"></i>
                    <strong>Remaining:</strong> {{ formatTime(uploadProgress.estimatedTimeRemaining) }}
                  </p>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="text-center mt-3">
                <button @click="pauseUpload" class="btn btn-warning btn-sm me-2" v-if="!uploadPaused">
                  <i class="bx bx-pause"></i> Pause
                </button>
                <button @click="resumeUpload" class="btn btn-success btn-sm me-2" v-if="uploadPaused">
                  <i class="bx bx-play"></i> Resume
                </button>
                <button @click="cancelUpload" class="btn btn-danger btn-sm">
                  <i class="bx bx-x"></i> Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Upload Configuration Section -->
          <div class="row mb-4" v-if="!uploading">
            <div class="col-md-3">
              <div class="form-group mb-3">
                <label>Select Sub County <span class="text-danger">*</span></label>
                <select class="form-select" @change="handleSubCountyChange" v-model="selectedSubCounty">
                  <option value="">Select Sub County</option>
                  <option v-for="(item, index) in subCounties" :key="index" :value="item">{{ item.subCountyName }}</option>
                </select>
              </div>
            </div>

            <div class="col-md-3">
              <div class="form-group mb-3">
                <label>Select Ward <span class="text-danger">*</span></label>
                <select class="form-select" v-model="selectedWard">
                  <option value="">Select Ward</option>
                  <option v-for="(item, index) in wards" :key="index" :value="item">{{ item.wardName }}</option>
                </select>
              </div>
            </div>

            <div class="col-md-3">
              <div class="form-group mb-3">
                <label>Select Zone/Market <span class="text-danger">*</span></label>
                <select class="form-select" v-model="selectedZone">
                  <option value="">Select Zone</option>
                  <option :key="index" :value="item.zone" v-for="(item, index) in zones">{{ item.zone }}</option>
                </select>
                <small class="text-muted">Or enter new:</small>
                <input v-model="newZone" type="text" class="form-control mt-1" placeholder="New zone name" @input="handleNewZone">
              </div>
            </div>

            <div class="col-md-3">
              <div class="form-group mb-3">
                <label>Batch Size</label>
                <select class="form-select" v-model="batchSize">
                  <option :value="10">10 records</option>
                  <option :value="25">25 records</option>
                  <option :value="50">50 records</option>
                  <option :value="100">100 records</option>
                </select>
                <small class="text-muted">Smaller = slower but more stable</small>
              </div>
            </div>
          </div>

          <!-- File Upload Section -->
          <div class="row mb-4" v-if="!fileData && !uploading">
            <div class="col-12">
              <div class="upload-area border border-2 border-dashed p-4 text-center"
                   @drop="handleDrop"
                   @dragover.prevent
                   @dragenter.prevent
                   style="border-color: #17a2b8; border-radius: 8px; cursor: pointer;">
                <input @change="handleFileChange($event)"
                       name="file"
                       type="file"
                       accept=".xlsx,.xls,.csv"
                       ref="fileInput"
                       style="display: none;">
                <div @click="$refs.fileInput.click()">
                  <div class="mb-3">
                    <i class="display-4 text-info bx bxs-cloud-upload"></i>
                  </div>
                  <h4>Drop files here or click to upload</h4>
                  <p class="text-muted">Supported formats: .xlsx, .xls, .csv</p>
                </div>
              </div>
            </div>
          </div>

          <!-- File Processing Loading -->
          <div class="row mb-4" v-if="processing">
            <div class="col-12 text-center">
              <div class="spinner-border text-info me-2" role="status"></div>
              <span>Processing file...</span>
            </div>
          </div>

          <!-- File Preview Section -->
          <div class="row" v-if="fileData && !processing && !uploading">
            <div class="col-12 mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">File Preview: {{ fileName }}</h6>
                <div>
                  <span class="badge bg-info me-2">{{ fileData.length }} rows</span>
                  <button @click="clearFile" class="btn btn-sm btn-outline-danger">
                    <i class="bx bx-x"></i> Clear
                  </button>
                </div>
              </div>
            </div>

            <!-- Validation Summary -->
            <div class="col-12 mb-3" v-if="validationSummary">
              <div class="alert" :class="validationSummary.hasErrors ? 'alert-warning' : 'alert-success'">
                <h6 class="alert-heading">Validation Summary</h6>
                <div class="row">
                  <div class="col-md-3"><strong>Total:</strong> {{ validationSummary.totalRows }}</div>
                  <div class="col-md-3"><strong>Valid:</strong> {{ validationSummary.validRows }}</div>
                  <div class="col-md-3"><strong>Invalid:</strong> {{ validationSummary.invalidRows }}</div>
                  <div class="col-md-3"><strong>Batches:</strong> {{ Math.ceil(validationSummary.validRows / batchSize) }}</div>
                </div>
              </div>
            </div>

            <!-- Data Table Preview -->
            <div class="col-12">
              <div class="table-responsive" style="max-height: 350px; overflow-y: auto;">
                <table class="table table-sm table-striped">
                  <thead class="table-dark sticky-top">
                  <tr>
                    <th>#</th>
                    <th v-for="(header, index) in headers.slice(0, 7)" :key="index">{{ header }}</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(row, index) in fileData.slice(0, 15)" :key="index" :class="row._hasErrors ? 'table-warning' : ''">
                    <td>{{ index + 1 }}</td>
                    <td v-for="(header, hIndex) in headers.slice(0, 7)" :key="hIndex">
                      {{ row[header] || '-' }}
                    </td>
                    <td>
                      <span v-if="row._hasErrors" class="badge bg-warning"><i class="bx bx-error"></i></span>
                      <span v-else class="badge bg-success"><i class="bx bx-check"></i></span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p class="text-muted text-center mt-2" v-if="fileData.length > 15">
                <small>Showing first 15 of {{ fileData.length }} rows</small>
              </p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="uploading">
            <i class="bx bx-x me-2"></i>Cancel
          </button>
          <button @click="handleUpload"
                  type="button"
                  class="btn btn-info"
                  :disabled="!canUpload || uploading"
                  v-if="!uploading">
            <i class="bx bx-check me-2"></i>Start Upload
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import * as XLSX from 'xlsx';
import { uploadTemplates } from "@/api";

export default {
  name: 'UploadPlotModal',
  props: {
    subCounties: { type: Array, default: () => [] },
    wards: { type: Array, default: () => [] },
    zones: { type: Array, default: () => [] }
  },
  emits: ['upload-batch', 'upload-complete', 'get-zones', 'get-wards'],
  data() {
    return {
      templateUrl: uploadTemplates + 'PLOTRENT-TEMPLATE.xlsx',
      selectedSubCounty: null,
      selectedWard: null,
      selectedZone: '',
      newZone: '',
      selectedFile: null,
      fileName: '',
      fileData: null,
      headers: [],
      processing: false,
      uploading: false,
      uploadPaused: false,
      uploadCancelled: false,
      validationSummary: null,
      requiredColumns: ['PLOT NO', 'OWNER NAME', 'ANNUAL RENT'],
      batchSize: 25,

      uploadProgress: {
        currentBatch: 0,
        totalBatches: 0,
        totalUploaded: 0,
        totalFailed: 0,
        totalRecords: 0,
        percentage: 0,
        startTime: null,
        elapsedTime: 0,
        estimatedTimeRemaining: 0
      },

      batchUploadState: {
        batches: [],
        currentBatchIndex: 0,
        allFailedRecords: [],
        uploadMetadata: null
      },

      uploadTimer: null
    }
  },
  computed: {
    canUpload() {
      return this.selectedSubCounty &&
          this.selectedWard &&
          (this.selectedZone || this.newZone) &&
          this.fileData &&
          this.fileData.length > 0 &&
          this.validationSummary &&
          this.validationSummary.validRows > 0;
    }
  },
  methods: {
    // ==================== STRING SANITIZATION ====================

    /**
     * Sanitize string to remove apostrophes and other characters
     * that may cause issues with backend SQL queries.
     *
     * Removes:
     * - Straight apostrophes (')
     * - Curly/smart apostrophes (', ', ′, `)
     * - Various quote characters (", ", ″)
     * - Backslashes (\)
     *
     * @param {string} value - The string to sanitize
     * @returns {string} - The sanitized string
     */
    sanitizeString(value) {
      if (value === null || value === undefined) return '';
      if (typeof value !== 'string') return String(value);

      return value
          // Remove various types of apostrophes
          .replace(/'/g, '')      // Straight apostrophe
          .replace(/'/g, '')      // Right single quotation mark
          .replace(/'/g, '')      // Left single quotation mark
          .replace(/′/g, '')      // Prime
          .replace(/`/g, '')      // Grave accent
          // Remove various types of quotes
          .replace(/"/g, '')      // Straight double quote
          .replace(/"/g, '')      // Left double quotation mark
          .replace(/"/g, '')      // Right double quotation mark
          .replace(/″/g, '')      // Double prime
          // Remove backslashes
          .replace(/\\/g, '')
          // Trim whitespace
          .trim();
    },

    /**
     * Sanitize all string fields in an object
     * @param {Object} obj - The object to sanitize
     * @returns {Object} - The sanitized object
     */
    sanitizeObject(obj) {
      if (!obj || typeof obj !== 'object') return obj;

      const sanitized = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (typeof value === 'string') {
            sanitized[key] = this.sanitizeString(value);
          } else {
            sanitized[key] = value;
          }
        }
      }
      return sanitized;
    },

    // ==================== EXISTING METHODS ====================

    handleSubCountyChange() {
      this.selectedZone = '';
      this.selectedWard = null;
      if (this.selectedSubCounty) {
        this.$emit('get-wards', this.selectedSubCounty.subCountyID);
        this.$emit('get-zones', this.selectedSubCounty.subCountyID);
      }
    },

    handleNewZone() {
      if (this.newZone.trim()) {
        // Sanitize the new zone name
        this.selectedZone = this.sanitizeString(this.newZone.trim().toUpperCase());
      }
    },

    handleDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files.length > 0) this.processFile(files[0]);
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) this.processFile(file);
    },

    processFile(file) {
      this.selectedFile = file;
      this.fileName = file.name;
      this.processing = true;
      this.fileData = null;
      this.headers = [];

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          let workbook;

          // Determine file type and read accordingly
          const fileExtension = file.name.split('.').pop().toLowerCase();

          if (fileExtension === 'csv') {
            // For CSV files
            workbook = XLSX.read(data, { type: 'binary' });
          } else {
            // For Excel files (xlsx, xls)
            workbook = XLSX.read(data, { type: 'binary', cellDates: true });
          }

          const firstSheetName = workbook.SheetNames[0];
          const firstSheet = workbook.Sheets[firstSheetName];

          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            defval: '',
            blankrows: false
          });

          if (jsonData && jsonData.length > 0) {
            this.processData(jsonData);
          } else {
            alert('No data found in file.');
          }
        } catch (error) {
          console.error('Error processing file:', error);
          alert('Error processing file: ' + error.message);
        } finally {
          this.processing = false;
        }
      };

      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        alert('Error reading file. Please try again.');
        this.processing = false;
      };

      // Read as binary string for better compatibility
      reader.readAsBinaryString(file);
    },

    processData(rawData) {
      if (!rawData || rawData.length === 0) {
        alert('No data found in file.');
        return;
      }

      // Auto-detect header row (find row containing 'PLOT' or 'NAME' or 'RENT')
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(rawData.length, 10); i++) {
        const row = rawData[i];
        if (row && Array.isArray(row)) {
          // Safely convert row to string, handling null/undefined values
          const rowStr = row
              .map(cell => (cell !== null && cell !== undefined) ? String(cell) : '')
              .join(' ')
              .toUpperCase();
          if (rowStr.includes('PLOT') || rowStr.includes('NAME') || rowStr.includes('RENT') || rowStr.includes('STALL')) {
            headerRowIndex = i;
            break;
          }
        }
      }

      // Extract headers and clean them
      const rawHeaders = rawData[headerRowIndex] || [];
      this.headers = rawHeaders.map((h, index) => {
        if (h === null || h === undefined || h === '') {
          return `Column_${index + 1}`;
        }
        return String(h).trim();
      });

      // Extract data rows (skip header and any rows before it)
      const dataRows = rawData.slice(headerRowIndex + 1);

      this.fileData = dataRows
          .filter(row => {
            // Filter out completely empty rows
            if (!row || !Array.isArray(row)) return false;
            return row.some(cell => cell !== null && cell !== undefined && cell !== '');
          })
          .map((row, index) => {
            const rowObject = {};
            this.headers.forEach((header, hIndex) => {
              let cellValue = row[hIndex];
              // Clean cell value
              if (cellValue === null || cellValue === undefined) {
                cellValue = '';
              } else if (typeof cellValue === 'number') {
                cellValue = cellValue;
              } else {
                // Sanitize string values to remove apostrophes
                cellValue = this.sanitizeString(String(cellValue).trim());
              }
              rowObject[header] = cellValue;
            });

            const validation = this.validateRow(rowObject, headerRowIndex + index + 2);
            rowObject._hasErrors = validation.hasErrors;
            rowObject._errors = validation.errors;
            return rowObject;
          });

      if (this.fileData.length === 0) {
        alert('No valid data rows found in file.');
        return;
      }

      this.generateValidationSummary();
    },

    validateRow(row, rowNumber) {
      const errors = {};
      let hasErrors = false;

      this.requiredColumns.forEach(col => {
        const found = this.headers.find(h => h.toUpperCase().includes(col));
        if (found && (!row[found] || row[found].toString().trim() === '')) {
          errors[col] = `${col} is required`;
          hasErrors = true;
        }
      });

      return { hasErrors, errors };
    },

    generateValidationSummary() {
      const totalRows = this.fileData.length;
      const invalidRows = this.fileData.filter(r => r._hasErrors).length;
      this.validationSummary = {
        totalRows,
        validRows: totalRows - invalidRows,
        invalidRows,
        hasErrors: invalidRows > 0
      };
    },

    clearFile() {
      this.selectedFile = null;
      this.fileName = '';
      this.fileData = null;
      this.headers = [];
      this.validationSummary = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.resetUploadProgress();
    },

    resetUploadProgress() {
      this.uploadProgress = {
        currentBatch: 0, totalBatches: 0, totalUploaded: 0, totalFailed: 0,
        totalRecords: 0, percentage: 0, startTime: null, elapsedTime: 0, estimatedTimeRemaining: 0
      };
      this.batchUploadState = { batches: [], currentBatchIndex: 0, allFailedRecords: [], uploadMetadata: null };
      if (this.uploadTimer) clearInterval(this.uploadTimer);
    },

    handleUpload() {
      // Sanitize zone name
      const zone = this.sanitizeString(this.selectedZone || this.newZone.trim().toUpperCase());

      if (!this.selectedSubCounty || !this.selectedWard || !zone) {
        alert('Please select Sub County, Ward and Zone');
        return;
      }

      if (!confirm(`Upload ${this.validationSummary.validRows} records to ${zone}?`)) return;

      this.startBatchUpload(zone);
    },

    startBatchUpload(zone) {
      this.uploading = true;
      this.uploadPaused = false;
      this.uploadCancelled = false;

      const validData = this.fileData.filter(r => !r._hasErrors).map(row => {
        const cleanRow = { ...row };
        delete cleanRow._hasErrors;
        delete cleanRow._errors;

        // Map columns flexibly
        const getCol = (patterns) => {
          for (const p of patterns) {
            const found = this.headers.find(h => h.toUpperCase().includes(p));
            if (found && row[found]) return row[found];
          }
          return '';
        };

        // Create row object with sanitized values
        const rowData = {
          plotNumber: this.sanitizeString(getCol(['PLOT NO', 'PLOT NUMBER', 'STALL']) || ''),
          ownerNames: this.sanitizeString(getCol(['OWNER NAME', 'NAME', 'TENANT']) || ''),
          ownerID: this.sanitizeString(getCol(['ID NO', 'ID NUMBER', 'IDNO']) || ''),
          ownerPhoneNo: this.sanitizeString(getCol(['PHONE', 'MOBILE', 'TEL']) || ''),
          ownerEmail: this.sanitizeString(getCol(['EMAIL']) || ''),
          annualRent: parseFloat(getCol(['ANNUAL RENT', 'RENT', 'RATE'])) || 0,
          arrearsForward: parseFloat(getCol(['ARREARS', 'ARREARS B/F', 'BALANCE B/F'])) || 0,
          accumulatedPenalty: parseFloat(getCol(['PENALTY', 'ACCUM PENALTY'])) || 0,
          plotSize: this.sanitizeString(getCol(['SIZE', 'PLOT SIZE']) || ''),
          plotAddress: this.sanitizeString(getCol(['ADDRESS', 'LOCATION']) || ''),
          zone: zone,
          subCountyID: this.selectedSubCounty.subCountyID,
          subCountyName: this.sanitizeString(this.selectedSubCounty.subCountyName),
          wardID: this.selectedWard.wardID,
          wardName: this.sanitizeString(this.selectedWard.wardName)
        };

        return rowData;
      });

      // Split into batches
      const batches = [];
      for (let i = 0; i < validData.length; i += this.batchSize) {
        batches.push(validData.slice(i, i + this.batchSize));
      }

      this.batchUploadState.batches = batches;
      this.batchUploadState.currentBatchIndex = 0;
      this.batchUploadState.allFailedRecords = [];
      this.batchUploadState.uploadMetadata = {
        zone,
        subCountyID: this.selectedSubCounty.subCountyID,
        subCountyName: this.sanitizeString(this.selectedSubCounty.subCountyName),
        wardID: this.selectedWard.wardID,
        wardName: this.sanitizeString(this.selectedWard.wardName),
        fileName: this.fileName
      };

      this.uploadProgress.totalBatches = batches.length;
      this.uploadProgress.totalRecords = validData.length;
      this.uploadProgress.startTime = Date.now();

      this.uploadTimer = setInterval(() => {
        if (!this.uploadPaused) {
          this.uploadProgress.elapsedTime = Math.floor((Date.now() - this.uploadProgress.startTime) / 1000);
          this.updateTimeEstimation();
        }
      }, 1000);

      this.uploadNextBatch();
    },

    async uploadNextBatch() {
      if (this.uploadCancelled || this.uploadPaused) return;

      if (this.batchUploadState.currentBatchIndex >= this.batchUploadState.batches.length) {
        this.finishUpload();
        return;
      }

      const batch = this.batchUploadState.batches[this.batchUploadState.currentBatchIndex];
      this.uploadProgress.currentBatch = this.batchUploadState.currentBatchIndex + 1;

      const payload = {
        plotData: batch,
        uploadMetadata: {
          ...this.batchUploadState.uploadMetadata,
          batchNumber: this.batchUploadState.currentBatchIndex + 1,
          totalBatches: this.batchUploadState.batches.length
        }
      };

      this.$emit('upload-batch', payload, this.handleBatchResponse);
    },

    handleBatchResponse(response) {
      // Safely handle response
      if (response && response.success && response.data) {
        this.uploadProgress.totalUploaded += response.data.successful || 0;
        this.uploadProgress.totalFailed += response.data.failed || 0;
        if (response.data.failedRecords && Array.isArray(response.data.failedRecords)) {
          this.batchUploadState.allFailedRecords.push(...response.data.failedRecords);
        }
      } else {
        // Get batch size safely
        const currentBatch = this.batchUploadState.batches[this.batchUploadState.currentBatchIndex];
        const batchSize = (currentBatch && Array.isArray(currentBatch)) ? currentBatch.length : 0;
        this.uploadProgress.totalFailed += batchSize;
      }

      this.batchUploadState.currentBatchIndex++;

      // Safely calculate percentage
      if (this.uploadProgress.totalBatches > 0) {
        this.uploadProgress.percentage = Math.round((this.batchUploadState.currentBatchIndex / this.uploadProgress.totalBatches) * 100);
      }

      // Check if more batches to process
      if (this.batchUploadState.currentBatchIndex < this.batchUploadState.batches.length) {
        setTimeout(() => this.uploadNextBatch(), 300);
      } else {
        this.finishUpload();
      }
    },

    updateTimeEstimation() {
      const processed = this.batchUploadState.currentBatchIndex;
      const remaining = this.uploadProgress.totalBatches - processed;
      if (processed > 0) {
        const avgTime = this.uploadProgress.elapsedTime / processed;
        this.uploadProgress.estimatedTimeRemaining = Math.round(avgTime * remaining);
      }
    },

    pauseUpload() { this.uploadPaused = true; },
    resumeUpload() { this.uploadPaused = false; this.uploadNextBatch(); },
    cancelUpload() {
      if (confirm('Cancel upload? Already uploaded records will remain.')) {
        this.uploadCancelled = true;
        this.finishUpload();
      }
    },

    finishUpload() {
      this.uploading = false;
      if (this.uploadTimer) clearInterval(this.uploadTimer);

      this.$emit('upload-complete', {
        successful: this.uploadProgress.totalUploaded,
        failed: this.uploadProgress.totalFailed,
        totalProcessed: this.uploadProgress.totalUploaded + this.uploadProgress.totalFailed,
        failedRecords: this.batchUploadState.allFailedRecords,
        cancelled: this.uploadCancelled,
        message: this.uploadCancelled
            ? `Cancelled. ${this.uploadProgress.totalUploaded} uploaded.`
            : `Completed. ${this.uploadProgress.totalUploaded} uploaded.`
      });

      setTimeout(() => { if (!this.uploading) this.clearFile(); }, 2000);
    },

    formatTime(seconds) {
      if (!seconds || seconds < 0) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  }
}
</script>

<style scoped>
.upload-area:hover { border-color: #0dcaf0 !important; }
.sticky-top { position: sticky; top: 0; z-index: 10; }
.stat-box { padding: 0.5rem; background: white; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
</style>